import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useQuery } from '@tanstack/react-query';
import type { PolicyTierDto } from '@ota/api-client';
import { fetchCancellationPolicy } from '../api';

interface Props {
  /** The property's (or booking's) assigned policy id. null/undefined = fully refundable. */
  policyId?: number | null;
  /** 'full' shows the tier breakdown; 'compact' shows a one-line summary. */
  variant?: 'full' | 'compact';
}

function tierWindow(hours: number): string {
  if (hours <= 0) return 'less than that';
  if (hours % 24 === 0) return `${hours / 24} day${hours / 24 === 1 ? '' : 's'}`;
  return `${hours} hours`;
}

/** Short headline: the most generous "free cancellation" tier, else "Non-refundable". */
function headline(tiers: PolicyTierDto[]): string {
  const full = tiers
    .filter((t) => Number(t.refundPercent) >= 100)
    .sort((a, b) => a.thresholdHours - b.thresholdHours)[0]; // smallest window still giving 100%
  if (full) return `Free cancellation up to ${tierWindow(full.thresholdHours)} before check-in`;
  const anyRefund = tiers.some((t) => Number(t.refundPercent) > 0);
  return anyRefund ? 'Partial refund available — see details' : 'Non-refundable';
}

export function CancellationPolicyInfo({ policyId, variant = 'full' }: Props) {
  const policy = useQuery({
    queryKey: ['cancellation-policy', policyId],
    queryFn: () => fetchCancellationPolicy(policyId!),
    enabled: !!policyId,
  });

  // No policy assigned → fully refundable.
  if (!policyId) {
    return variant === 'compact' ? (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'success.main' }}>
        <VerifiedUserIcon fontSize="small" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Free cancellation</Typography>
      </Stack>
    ) : (
      <Alert severity="success" icon={<VerifiedUserIcon />}>
        <strong>Fully refundable</strong> — cancel any time before check-in for a full refund.
      </Alert>
    );
  }

  if (policy.isLoading) return <CircularProgress size={18} />;
  if (!policy.data) return null;

  const tiers = [...policy.data.tiers].sort((a, b) => b.thresholdHours - a.thresholdHours);
  const head = headline(tiers);
  const nonRefundable = head === 'Non-refundable';

  if (variant === 'compact') {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center"
        sx={{ color: nonRefundable ? 'text.secondary' : 'success.main' }}>
        {nonRefundable ? <EventBusyIcon fontSize="small" /> : <VerifiedUserIcon fontSize="small" />}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{head}</Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {nonRefundable
          ? <EventBusyIcon color="action" />
          : <VerifiedUserIcon sx={{ color: 'success.main' }} />}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{policy.data.name}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{head}</Typography>
      {policy.data.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {policy.data.description}
        </Typography>
      )}
      <Stack spacing={0.75}>
        {tiers.map((t, i) => (
          <Stack key={i} direction="row" justifyContent="space-between"
            sx={{ maxWidth: 420, borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t.thresholdHours > 0
                ? `Cancel ${tierWindow(t.thresholdHours)}+ before check-in`
                : 'Cancel later / no-show'}
            </Typography>
            <Chip
              size="small"
              label={`${Number(t.refundPercent)}% refund`}
              color={Number(t.refundPercent) >= 100 ? 'success'
                : Number(t.refundPercent) > 0 ? 'warning' : 'default'}
              variant={Number(t.refundPercent) > 0 ? 'filled' : 'outlined'}
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
